import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation, MapPin, Car, Footprints, Train, Crosshair, Loader2, ArrowUpRight, AlertTriangle, MessageCircle, Building2, StickyNote } from "lucide-react";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { trackEvent } from "@/lib/tracking";
import {
  ENTRANCE,
  REGISTERED,
  buildDirectionsUrl,
  buildWhatsAppDirectionsUrl,
  trackDirectionsClick,
  type DestinationKind,
} from "@/lib/location-links";

type TravelMode = "driving" | "walking" | "transit";

interface Props {
  /** Tracking source label, e.g. "homepage", "find_us", "area_poplar". */
  source?: string;
  /** Optional heading override. */
  title?: string;
  /** Compact variant for sidebar use. */
  compact?: boolean;
  /** Landing page identifier — surfaced in every tracking event for per-page breakdown. */
  landingPage?: string;
}

const MODE_META: Record<TravelMode, { label: string; icon: typeof Car }> = {
  driving: { label: "Drive", icon: Car },
  walking: { label: "Walk", icon: Footprints },
  transit: { label: "Transit", icon: Train },
};

/**
 * Lightweight embedded "directions" widget.
 *
 * - Highlights the yard entrance (separate from registered address)
 * - Lets visitors enter a starting point or use their current location
 * - Lets visitors toggle between yard entrance and registered address pins
 * - Lets visitors WhatsApp for written directions when they can't use Maps
 * - Fires structured tracking for mode change, origin set, and start route
 */
export function DirectionsWidget({ source = "directions_widget", title = "Get directions to the workshop", compact, landingPage }: Props) {
  const { compliance, routing } = usePublicConfig();
  const [origin, setOrigin] = useState("");
  const [mode, setMode] = useState<TravelMode>("driving");
  const [destinationKind, setDestinationKind] = useState<DestinationKind>("entrance");
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [originSetOnce, setOriginSetOnce] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");

  const page = landingPage || (typeof window !== "undefined" ? window.location.pathname : "unknown");

  function buildUrl(useOrigin: string) {
    return buildDirectionsUrl(destinationKind, { origin: useOrigin, mode });
  }

  function handleStartRoute(e: React.MouseEvent<HTMLAnchorElement>) {
    const url = buildUrl(origin);
    e.currentTarget.setAttribute("href", url);
    const originType = origin.trim() ? "manual" : "default";
    // Granular start route event (per landing page)
    trackEvent("directions_start_route", {
      source,
      landing_page: page,
      destination: destinationKind,
      mode,
      origin_type: originType,
      has_origin: origin.trim().length > 0,
    });
    // Maintain backwards-compat directions_click for legacy dashboards.
    trackDirectionsClick({
      source,
      kind: destinationKind,
      mode,
      origin_type: originType,
      action: "start_route",
    });
  }

  function handleModeChange(next: TravelMode) {
    if (next === mode) return;
    setMode(next);
    trackEvent("directions_mode_change", {
      source,
      landing_page: page,
      destination: destinationKind,
      mode: next,
      previous_mode: mode,
    });
  }

  function handleDestinationChange(next: DestinationKind) {
    if (next === destinationKind) return;
    setDestinationKind(next);
    trackEvent("directions_mode_change", {
      source,
      landing_page: page,
      destination: next,
      mode,
      action: "destination_change",
    });
  }

  function handleOriginBlur() {
    if (!origin.trim() || originSetOnce) return;
    setOriginSetOnce(true);
    trackEvent("directions_origin_set", {
      source,
      landing_page: page,
      destination: destinationKind,
      mode,
      origin_type: "manual",
    });
  }

  function useMyLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("Location not available — type a postcode instead.");
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin(`${pos.coords.latitude.toFixed(5)},${pos.coords.longitude.toFixed(5)}`);
        setLocating(false);
        setOriginSetOnce(true);
        trackEvent("directions_origin_set", {
          source,
          landing_page: page,
          destination: destinationKind,
          mode,
          origin_type: "geolocation",
        });
      },
      (err) => {
        setLocating(false);
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied — type a postcode instead."
            : "Couldn't find your location — type a postcode instead."
        );
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  }

  const whatsappHref = buildWhatsAppDirectionsUrl(routing.whatsappNumber, source, {
    kind: destinationKind,
    note: note.trim() || undefined,
    origin: origin.trim() || undefined,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`rounded-3xl border border-border bg-card shadow-xl shadow-black/[0.04] overflow-hidden ${compact ? "" : ""}`}
    >
      {/* Header strip */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-border px-5 md:px-6 py-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
            <Navigation className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">Directions</p>
            <h3 className="mt-0.5 text-base md:text-lg font-bold text-foreground tracking-tight leading-tight">
              {title}
            </h3>
          </div>
        </div>
      </div>

      {/* Destination switch — entrance vs registered */}
      <div className="px-5 md:px-6 pt-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Pin location</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => handleDestinationChange("entrance")}
            className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
              destinationKind === "entrance"
                ? "border-primary bg-primary/10 shadow-sm shadow-primary/10"
                : "border-border bg-background hover:border-primary/30"
            }`}
            aria-pressed={destinationKind === "entrance"}
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              <Navigation className="h-3 w-3" /> Yard entrance
            </span>
            <span className="block mt-0.5 text-[11px] font-semibold text-foreground leading-tight">
              {ENTRANCE.street}, {ENTRANCE.postcode}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleDestinationChange("registered")}
            className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
              destinationKind === "registered"
                ? "border-primary bg-primary/10 shadow-sm shadow-primary/10"
                : "border-border bg-background hover:border-primary/30"
            }`}
            aria-pressed={destinationKind === "registered"}
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              <Building2 className="h-3 w-3" /> Registered
            </span>
            <span className="block mt-0.5 text-[11px] font-semibold text-foreground leading-tight">
              {REGISTERED.street}, {REGISTERED.postcode}
            </span>
          </button>
        </div>
      </div>

      {/* Entrance highlight */}
      <div className="px-5 md:px-6 pt-4">
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 text-xs text-foreground leading-relaxed">
              <p className="font-bold uppercase tracking-wider text-[10px] text-amber-700 dark:text-amber-300">
                {destinationKind === "entrance" ? "You'll arrive at the yard gate" : "Heads-up — registered address only"}
              </p>
              <p className="mt-1.5 text-muted-foreground">
                {destinationKind === "entrance" ? (
                  <>The registered address is <span className="font-semibold text-foreground">{compliance.streetAddress}</span>, but Google sometimes drops the pin on the wrong side. <span className="font-semibold text-foreground">Sat-nav set to {ENTRANCE.fullAddress}</span> (coords {ENTRANCE.lat}, {ENTRANCE.lng}).</>
                ) : (
                  <>This pin lands on the Pixley Street side. The actual driveway is round the corner — switch to <button type="button" onClick={() => handleDestinationChange("entrance")} className="underline font-semibold text-foreground hover:text-primary">Yard entrance</button> for the gate.</>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Origin input */}
      <div className="px-5 md:px-6 pt-4 pb-5 space-y-3">
        <div>
          <label htmlFor={`dir-origin-${source}`} className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
            Starting from
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id={`dir-origin-${source}`}
                type="text"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                onBlur={handleOriginBlur}
                placeholder="Postcode, area or address (optional)"
                className="w-full rounded-xl border border-border bg-background pl-10 pr-3 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
              />
            </div>
            <button
              type="button"
              onClick={useMyLocation}
              disabled={locating}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-3 py-3 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-60"
              aria-label="Use my current location"
              title="Use my current location"
            >
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
              <span className="hidden sm:inline">{locating ? "Locating…" : "Use my location"}</span>
            </button>
          </div>
          {geoError && (
            <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">{geoError}</p>
          )}
        </div>

        {/* Travel mode pills */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Travel mode</p>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(MODE_META) as TravelMode[]).map((m) => {
              const Icon = MODE_META[m].icon;
              const active = mode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleModeChange(m)}
                  className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-xs font-bold transition-all ${
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow-sm shadow-primary/20"
                      : "border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/30"
                  }`}
                  aria-pressed={active}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {MODE_META[m].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Start route CTA */}
        <a
          href={buildUrl(origin)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleStartRoute}
          className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
        >
          <Navigation className="h-4 w-4" />
          Start route in Google Maps
          <ArrowUpRight className="h-3.5 w-3.5" />
        </a>

        {/* Optional booking / vehicle note (shown above WhatsApp CTA) */}
        <div className="rounded-xl border border-border bg-background/60 p-3">
          <button
            type="button"
            onClick={() => setShowNote((v) => !v)}
            className="w-full flex items-center justify-between gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            aria-expanded={showNote}
          >
            <span className="inline-flex items-center gap-1.5">
              <StickyNote className="h-3.5 w-3.5" />
              {showNote ? "Add a note" : "Add a vehicle / booking note (optional)"}
            </span>
            <span className="text-foreground">{showNote ? "–" : "+"}</span>
          </button>
          {showNote && (() => {
            const NOTE_MAX = 240;
            // Sanitise: strip control chars + collapse runs of whitespace so the
            // pre-filled WhatsApp message stays on one or two readable lines.
            const sanitise = (raw: string) =>
              raw
                .replace(/[\u0000-\u001F\u007F]/g, " ")
                .replace(/<[^>]*>/g, "")
                .replace(/\s{2,}/g, " ");
            const remaining = NOTE_MAX - note.length;
            const tooShort = note.trim().length > 0 && note.trim().length < 3;
            const nearLimit = remaining <= 30;
            return (
              <div className="mt-2">
                <textarea
                  value={note}
                  onChange={(e) => setNote(sanitise(e.target.value).slice(0, NOTE_MAX))}
                  placeholder="e.g. AB12 CDE — booked for MOT Tuesday 10am"
                  rows={2}
                  maxLength={NOTE_MAX}
                  aria-invalid={tooShort}
                  className={`w-full rounded-lg border bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 resize-none ${
                    tooShort ? "border-amber-500/50" : "border-border"
                  }`}
                />
                <div className="mt-1 flex items-center justify-between text-[10px]">
                  <p className={tooShort ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"}>
                    {tooShort
                      ? "A bit more detail helps — at least 3 characters"
                      : "Letters, numbers and spaces only — keeps the message readable"}
                  </p>
                  <p className={`tabular-nums ${nearLimit ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"}`}>
                    {note.length}/{NOTE_MAX}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* WhatsApp directions fallback — pre-fills selected pin + optional note */}
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackEvent("directions_whatsapp_click", {
              source,
              landing_page: page,
              destination: destinationKind,
              has_note: note.trim().length > 0,
            })
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#25D366]/30 bg-[#25D366]/[0.06] px-4 py-3 text-xs font-bold text-[#1ea952] hover:bg-[#25D366]/10 transition-all"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Can't use Maps? WhatsApp the {destinationKind === "entrance" ? "yard entrance" : "registered address"} directions
        </a>

        <p className="text-[11px] text-muted-foreground text-center">
          Opens in Google Maps · works with Apple Maps & Waze too
        </p>
      </div>
    </motion.div>
  );
}
